import type { RubricDetailDto, RubricVersionDetailDto } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { Plus } from 'lucide-react';
import { PageHeader } from '../../../../components/page-header';

interface RubricDetailProps {
  rubric: RubricDetailDto;
  onCreateVersion: () => void;
}

/**
 * Category chip in the prototype's style: the negative/positive/info signal is
 * the chip itself; a reviewer note rides inside it as "negative · <note>".
 */
function FlagCategoryChip({
  category,
  note,
}: {
  category: 'POSITIVE' | 'NEGATIVE' | 'INFORMATIONAL';
  note: string | null;
}) {
  const tone = {
    NEGATIVE: 'bg-warning/15 text-warning ring-warning/25',
    POSITIVE: 'bg-success/15 text-success ring-success/25',
    INFORMATIONAL: 'bg-muted text-muted-foreground ring-border',
  }[category];
  const label = category === 'INFORMATIONAL' ? 'info' : category.toLowerCase();
  return (
    <span
      className={
        'inline-flex max-w-[60%] items-baseline gap-1 truncate rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        tone
      }
      title={note ?? undefined}
    >
      {label}
      {note && <span className="truncate font-normal opacity-80">· {note}</span>}
    </span>
  );
}

export function RubricDetail({ rubric, onCreateVersion }: RubricDetailProps) {
  const latestVersion: RubricVersionDetailDto | undefined = rubric.versions[0];
  const pillButtonClass =
    'h-8 px-3 text-xs [&_svg]:size-3.5 sm:h-9 sm:px-4 sm:text-sm sm:[&_svg]:size-4';

  return (
    <div className="space-y-12">
      <PageHeader
        size="compact"
        title={rubric.name}
        description={rubric.description ?? undefined}
        meta={
          latestVersion ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
              <span className="tabular-nums">v{latestVersion.version}</span>
            </span>
          ) : null
        }
        action={
          <Button
            variant="outline"
            shape="pill"
            size="sm"
            className={pillButtonClass}
            onClick={onCreateVersion}
          >
            <Plus strokeWidth={1.6} />
            New version
          </Button>
        }
      />

      {latestVersion && (
        <>
          <section className="space-y-5">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Dimensions
            </h2>
            <div className="overflow-x-auto rounded-lg border border-border/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 bg-muted/30">
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Name
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Key
                    </th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Weight
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Low anchor
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Mid anchor
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      High anchor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {latestVersion.dimensions.map((dim) => (
                    <tr key={dim.key} className="bg-card/40">
                      <td className="px-4 py-3 font-medium text-foreground">{dim.name}</td>
                      <td className="px-4 py-3">
                        <code className="font-mono text-[12px] text-foreground/80">{dim.key}</code>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                        {dim.weight}%
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-muted-foreground max-w-[200px]">
                        {dim.anchors.low}
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-muted-foreground max-w-[200px]">
                        {dim.anchors.mid}
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-muted-foreground max-w-[200px]">
                        {dim.anchors.high}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-5">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Score bands
            </h2>
            <div className="overflow-x-auto rounded-lg border border-border/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 bg-muted/30">
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Label
                    </th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Min
                    </th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      Range
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {latestVersion.bands.map((band, i) => {
                    // Bands are ordered highest to lowest, so row i's upper bound is the
                    // previous band's min (or 5.0 for the top band).
                    const upperBound = i === 0 ? '5.0' : latestVersion.bands[i - 1].min;
                    const range = `${band.min} – ${upperBound}`;
                    return (
                      <tr key={`${band.label}-${band.min}`} className="bg-card/40">
                        <td className="px-4 py-3 font-medium text-foreground">{band.label}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {band.min}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">{range}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {latestVersion.flags.length > 0 && (
            <section className="space-y-5">
              <div className="space-y-1">
                <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
                  Flags
                </h2>
                <p className="text-[13px] text-muted-foreground">
                  Tracked separately from the score so one issue doesn&apos;t quietly drag down a
                  good candidate. Each fired flag cites transcript evidence.
                </p>
              </div>
              <div className="rounded-lg border border-border/70 bg-card/40 px-4 sm:px-5">
                <ul className="divide-y divide-border/60">
                  {latestVersion.flags.map((flag) => (
                    <li key={flag.key} className="py-4 space-y-1.5">
                      <div className="flex items-baseline justify-between gap-4">
                        <code className="font-mono text-[12.5px] font-medium tracking-tight text-foreground">
                          {flag.key}
                        </code>
                        <FlagCategoryChip category={flag.category} note={flag.note ?? null} />
                      </div>
                      <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                        {flag.guidance}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </>
      )}

      <section className="space-y-5">
        <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
          Versions
        </h2>
        {rubric.versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions have been created yet.</p>
        ) : (
          <ul className="divide-y divide-border/70 border-y border-border/70">
            {rubric.versions.map((version) => {
              const isLatest = version.id === latestVersion?.id;
              return (
                <li
                  key={version.id}
                  className="flex items-center justify-between gap-6 py-4 text-sm"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-base text-foreground tabular-nums">
                      v{version.version}
                    </span>
                    {isLatest && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/60 px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
                        <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
                        Latest
                      </span>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {version.dimensions.length}{' '}
                      {version.dimensions.length === 1 ? 'dimension' : 'dimensions'}
                    </Badge>
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {new Date(version.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
